import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { createBusinessEntity } from '~apis/business-entity';
import {
  BusinessEntityDetailRequest,
  BusinessEntityListRequest,
  BusinessEntitySummaryRequest,
  deleteOrMergeBusinessEntity,
  getBusinessEntities,
  getBusinessEntitiesSummary,
  getBusinessEntityDetail,
  getBusinessEntityDetailForContracts,
  setFavoriteBusinessEntity,
  updateBusinessEntity,
  updateOpeningBalanceBulk,
} from '~apis/contract';
import { queryClient } from '~libs/queryClient';

import { CARD_QUERY_KEY } from '../card/query';
import { BusinessEntityQueryKey } from '../useBusinessEntityListQuery';

export const ContractQueryKey = {
  default: ['contract'],
  allList: () => [...ContractQueryKey.default, 'list'],
  list: (params: BusinessEntityListRequest) => [
    ...ContractQueryKey.allList(),
    params.companyId,
    params.query,
  ],
  summary: (params: BusinessEntitySummaryRequest) => [
    'contract',
    'summary',
    params.companyId,
    params.query,
  ],
  detailContract: (params: BusinessEntityDetailRequest) => [
    'contract',
    params.companyId,
    params.businessEntityId,
    params.endDate,
    params.startDate,
  ],
  detail: (params: { companyId: string; id: number }) => ['contract', params.companyId, params.id],
};

export function useBusinessEntities(params: BusinessEntityListRequest) {
  return useQuery({
    queryKey: ContractQueryKey.list(params),
    queryFn: () => getBusinessEntities(params),
    refetchOnMount: true,
    enabled: !!params.companyId,
  });
}

export function useBusinessEntitiesSummary(params: BusinessEntitySummaryRequest) {
  return useQuery({
    queryKey: ContractQueryKey.summary(params),
    queryFn: () => getBusinessEntitiesSummary(params),
    enabled: !!params.companyId,
  });
}

export function useBusinessEntityDetail(params: BusinessEntityDetailRequest) {
  const router = useRouter();
  return useQuery({
    queryKey: ContractQueryKey.detailContract(params),
    queryFn: () => getBusinessEntityDetailForContracts(params),
    refetchOnMount: true,
    enabled:
      Boolean(params.companyId) &&
      Boolean(params.startDate) &&
      Boolean(params.endDate) &&
      Boolean(params.businessEntityId),
    onSuccess: (data) => {
      // 데이터가 성공적으로 조회되었지만 실제 데이터가 없는 경우 콜백 실행
      if (!data || !data.Data?.data) {
        router.push('/clobe/sales-purchase');
      }
    },
  });
}

export function useBusinessEntityDetails(params: { companyId: string; id: number }) {
  return useQuery({
    queryKey: ContractQueryKey.detail(params),
    queryFn: () => getBusinessEntityDetail(params),
    enabled: !!params.companyId && !!params.id,
  });
}

export function useSetFavoriteBusinessEntity() {
  return useMutation({
    mutationFn: setFavoriteBusinessEntity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(ContractQueryKey.list(variables));
    },
  });
}

export function useDeleteOrMergeBusinessEntity() {
  return useMutation({
    mutationFn: deleteOrMergeBusinessEntity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(ContractQueryKey.list(variables));
    },
  });
}

export function useCreateBusinessEntity() {
  return useMutation({
    mutationFn: createBusinessEntity,
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries(ContractQueryKey.allList());
      queryClient.invalidateQueries(BusinessEntityQueryKey.default);
      queryClient.invalidateQueries(CARD_QUERY_KEY.cardBusinessEntities(companyId));
    },
  });
}

export function useUpdateBusinessEntity() {
  return useMutation({
    mutationFn: updateBusinessEntity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        ContractQueryKey.detail({ companyId: variables.companyId, id: variables.request.id }),
      );

      queryClient.invalidateQueries(
        BusinessEntityQueryKey.list({
          companyId: variables.companyId,
          favoriteOnly: false,
        }),
      );
    },
  });
}

export function useUpdateOpeningBalance() {
  return useMutation({
    mutationFn: updateOpeningBalanceBulk,
  });
}
